// ===================================
// PAYMENT SUCCESS PAGE - FIXED WITH INLINE STYLES
// src/pages/PaymentSuccess.js
// Uses inline styles to ensure correct centered layout
// ===================================

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const stripeSessionId = searchParams.get('stripe_session_id');
  const sessionId = searchParams.get('session_id');
  const isSimulated = searchParams.get('simulated') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const MAX_RETRIES = 3;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isSimulated && sessionId) {
      fetchSessionData(sessionId);
      return;
    }

    if (stripeSessionId) {
      verifyStripePayment();
      return;
    }

    setError('No payment session found');
    setLoading(false);
  }, [stripeSessionId, sessionId, isSimulated, isAuthenticated]);

  const fetchSessionData = async (sid) => {
    try {
      setLoading(true);
      const response = await api.get(`/payments/session/${sid}`);
      
      if (response.data) {
        setSessionData({
          session_id: sid,
          access_code: response.data.access_code || response.data.session?.access_code,
          interview_type: response.data.session?.interview_type,
          duration: response.data.session?.duration,
          simulated: true
        });
      } else {
        throw new Error('No session data returned');
      }
    } catch (err) {
      console.error('Failed to fetch session:', err);
      if (retryCount < 2) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchSessionData(sid), 1000);
        return;
      }
      setSessionData({ session_id: sid, simulated: true });
    } finally {
      setLoading(false);
    }
  };

  const verifyStripePayment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/payments/verify-stripe-session', {
        stripe_session_id: stripeSessionId
      });
      
      if (response.data.success) {
        setSessionData({
          session_id: response.data.session_id,
          access_code: response.data.access_code,
          message: response.data.message
        });
      } else {
        throw new Error(response.data.message || 'Verification failed');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => verifyStripePayment(), 2000);
        return;
      }
      setError(err.response?.data?.message || err.message || 'Failed to verify payment');
    } finally {
      if (retryCount >= MAX_RETRIES || !error) {
        setLoading(false);
      }
    }
  };

  const handleCopyCode = async () => {
    if (sessionData?.access_code) {
      try {
        await navigator.clipboard.writeText(sessionData.access_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = sessionData.access_code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  // ===================================
  // INLINE STYLES
  // ===================================
  const styles = {
    page: {
      minHeight: 'calc(100vh - 200px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    },
    card: {
      maxWidth: '520px',
      width: '100%',
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '20px',
      padding: '3rem 2.5rem',
      textAlign: 'center'
    },
    icon: {
      fontSize: '4rem',
      marginBottom: '1.5rem',
      display: 'block'
    },
    title: {
      fontFamily: "'Playfair Display', serif",
      fontSize: '2.25rem',
      color: '#fff',
      margin: '0 0 0.5rem'
    },
    subtitle: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '1.1rem',
      margin: '0 0 2rem'
    },
    simulatedNotice: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      background: 'rgba(59, 130, 246, 0.1)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '8px',
      color: '#60a5fa',
      fontSize: '0.85rem',
      marginBottom: '2rem'
    },
    // Access Code Box - CENTERED VERTICAL LAYOUT
    accessCodeBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.12), rgba(212, 175, 55, 0.04))',
      border: '2px solid rgba(212, 175, 55, 0.25)',
      borderRadius: '16px',
      padding: '2rem',
      marginBottom: '2rem'
    },
    codeLabel: {
      display: 'block',
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.5)',
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      marginBottom: '1rem',
      fontWeight: '500'
    },
    codeRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '1rem'
    },
    codeValue: {
      fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#d4af37',
      letterSpacing: '0.15em',
      textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)'
    },
    copyBtn: {
      background: copied ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.08)',
      border: copied ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid rgba(255, 255, 255, 0.15)',
      borderRadius: '10px',
      padding: '0.625rem 0.75rem',
      fontSize: '1.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease'
    },
    codeHint: {
      fontSize: '0.875rem',
      color: 'rgba(255, 255, 255, 0.45)',
      margin: '0',
      fontStyle: 'italic'
    },
    // Buttons
    actions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.875rem',
      marginBottom: '2rem'
    },
    btnPrimary: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '1.125rem 2rem',
      background: 'linear-gradient(135deg, #d4af37, #b8962e)',
      color: '#000',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      textDecoration: 'none',
      boxShadow: '0 4px 15px rgba(212, 175, 55, 0.25)'
    },
    btnSecondary: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      background: 'rgba(255, 255, 255, 0.06)',
      color: '#fff',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    // Next Steps
    nextSteps: {
      textAlign: 'left',
      paddingTop: '1.75rem',
      borderTop: '1px solid rgba(255, 255, 255, 0.08)'
    },
    nextStepsTitle: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.5)',
      margin: '0 0 1.25rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontStyle: 'italic'
    },
    stepsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    stepItem: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '1rem'
    },
    stepNumber: {
      width: '36px',
      height: '36px',
      minWidth: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #d4af37, #b8962e)',
      borderRadius: '50%',
      fontSize: '0.9rem',
      fontWeight: '700',
      color: '#000',
      flexShrink: '0'
    },
    stepText: {
      color: 'rgba(255, 255, 255, 0.75)',
      fontSize: '0.95rem',
      lineHeight: '1.4',
      textAlign: 'left',
      flex: '1'
    },
    // Loading
    spinner: {
      width: '50px',
      height: '50px',
      border: '3px solid rgba(255, 255, 255, 0.1)',
      borderTopColor: '#d4af37',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      margin: '0 auto 1.5rem'
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ padding: '2rem 0', textAlign: 'center' }}>
            <div style={styles.spinner}></div>
            <h2 style={{ ...styles.title, fontSize: '1.25rem', marginBottom: '0.5rem' }}>
              {stripeSessionId ? 'Verifying your payment...' : 'Loading session...'}
            </h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>
              Please wait while we confirm your booking
            </p><br/>
            {retryCount > 0 && (
              <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.4)', marginTop: '1rem' }}>
                Attempt {retryCount}/{MAX_RETRIES}
              </p>
            )}
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, borderColor: 'rgba(239, 68, 68, 0.3)' }}>
          <div style={styles.icon}>❌</div>
          <h2 style={{ ...styles.title, fontSize: '1.5rem' }}>Payment Verification Issue</h2>
          <p style={{ color: '#f87171', fontSize: '1rem', margin: '0 0 1rem' }}>{error}</p>
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem', margin: '0 0 1.5rem', lineHeight: '1.5' }}>
            If you were charged, don't worry! Your session may still be created. 
            Please check your Sessions page or contact support.
          </p>
          <div style={styles.actions}>
            <button 
              onClick={() => {
                setRetryCount(0);
                setError(null);
                if (stripeSessionId) verifyStripePayment();
                else if (sessionId) fetchSessionData(sessionId);
              }}
              style={styles.btnSecondary}
            >
              Try Again
            </button>
            <Link to="/app/sessions" style={styles.btnPrimary}>
              View Sessions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.icon}>🎉</div>
        <h1 style={styles.title}>Payment Successful!</h1>
        <p style={styles.subtitle}>Your interview session has been booked successfully.</p>
        
        {sessionData?.simulated && (
          <div style={styles.simulatedNotice}>
            <span>ℹ️</span>
            <span>Test mode - No actual payment was processed</span>
          </div>
        )}

        {/* Access Code Display - VERTICAL CENTERED */}
        {sessionData?.access_code ? (
          <div style={styles.accessCodeBox}>
            <span style={styles.codeLabel}>Your Access Code</span>
            <div style={styles.codeRow}>
              <span style={styles.codeValue}>{sessionData.access_code}</span>
              <button 
                style={styles.copyBtn}
                onClick={handleCopyCode}
                title={copied ? 'Copied!' : 'Copy code'}
              >
                {copied ? '✓' : '📋'}
              </button>
            </div>
            <span style={styles.codeHint}>Save this code — you'll need it to start your interview</span>
          </div>
        ) : (
          <div style={{ padding: '1.25rem', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '12px', marginBottom: '2rem' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              Your access code will be available in your Sessions page.
            </p>
          </div>
        )}


{/* {sessionData?.access_code && (
  <div style={{
    marginTop: '1.5rem',
    padding: '1rem',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.08)'
  }}>
    <p style={{ 
      margin: 0, 
      fontSize: '0.85rem', 
      color: 'rgba(255, 255, 255, 0.5)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap'
    }}>
      <span>🔑</span>
      <span>Your access code:</span>
      <strong style={{ color: '#fbbf24', fontFamily: 'monospace' }}>{sessionData.access_code}</strong>
      <button 
        onClick={handleCopyCode}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          color: 'rgba(255, 255, 255, 0.5)'
        }}
      >
        {copied ? '✓' : '📋'}
      </button>
    </p>
    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.4)' }}>
      Save this code if you need to resume your session from another device
    </p>
  </div>
)} */}
        {/* Action Buttons */}
      <div style={styles.actions}>
  {sessionData?.session_id ? (
    <Link 
      to={`/app/interview/${sessionData.session_id}`} 
      style={styles.btnPrimary}
    >
      🎯 Start Interview Now
    </Link>
  ) : sessionData?.access_code ? (
    <Link 
      to={`/app/start-session?code=${sessionData.access_code}`} 
      style={styles.btnPrimary}
    >
      🎯 Start Interview Now
    </Link>
  ) : null}
  <Link to="/app/sessions" style={styles.btnSecondary}>
    View All Sessions
  </Link>
</div>

        {/* Next Steps */}
     <div style={styles.nextSteps}>
  <h3 style={styles.nextStepsTitle}>What's Next?</h3>
  <div style={styles.stepsList}>
    <div style={styles.stepItem}>
      <span style={styles.stepNumber}>1</span>
      <span style={styles.stepText}>Click "Start Interview" above</span>
    </div>
    <div style={styles.stepItem}>
      <span style={styles.stepNumber}>2</span>
      <span style={styles.stepText}>Your AI interviewer will greet you</span>
    </div>
    <div style={styles.stepItem}>
      <span style={styles.stepNumber}>3</span>
      <span style={styles.stepText}>Answer questions and get feedback!</span>
    </div>
  </div>
</div>
      </div>
      
      {/* Keyframes for spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PaymentSuccess;